import { Box, Button, Dialog, DialogContent, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { useId, useState } from 'react'
import GameButton from '@/components/game/GameButton.jsx'
import {
    BODY_FONT,
    GAME,
    HEADING_SX,
    rise,
    STILL_FOR_REDUCED_MOTION
} from '@/components/game/gamey.js'
import JoulelingoCharacter from '@/components/game/JoulelingoCharacter.jsx'
import { useFullScreenDialog } from '@/hooks/useFullScreenDialog'
import { meetJoltSteps } from './meetJolt'

/**
 * Meet Jolt: the first-run welcome for a player on joulelingo.com, in place of the generic
 * one. A few short steps with the character, then `onDone`, which marks onboarding finished
 * exactly as the generic welcome does. Skipping finishes it too, so nobody sees it twice.
 */
export default function MeetJolt({ quizOn = false, saving = false, onDone }) {
    const fullScreen = useFullScreenDialog()
    const titleId = useId()
    const steps = meetJoltSteps({ quizOn })
    const [index, setIndex] = useState(0)
    const step = steps[index]
    const last = index === steps.length - 1

    return (
        <Dialog
            fullScreen={fullScreen}
            open
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown
            aria-labelledby={titleId}
            slotProps={{
                backdrop: { sx: { backdropFilter: 'blur(4px)' } },
                paper: {
                    sx: {
                        bgcolor: GAME.mint,
                        border: `2px solid ${GAME.mintDeep}`,
                        borderRadius: fullScreen ? 0 : '28px',
                        boxShadow: `0 8px 0 ${GAME.mintDeep}`
                    }
                }
            }}
        >
            <DialogContent
                data-meet-jolt={step.key}
                sx={{
                    p: { xs: 3, sm: 5 },
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    gap: 2,
                    fontFamily: BODY_FONT,
                    color: GAME.ink,
                    '& .MuiTypography-root': { fontFamily: BODY_FONT }
                }}
            >
                <Box
                    key={step.key}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2,
                        animation: `${rise} 360ms cubic-bezier(0.2,0.8,0.2,1) both`,
                        ...STILL_FOR_REDUCED_MOTION
                    }}
                >
                    <Box
                        sx={{
                            width: 168,
                            height: 168,
                            borderRadius: '50%',
                            bgcolor: GAME.mintDeep,
                            display: 'grid',
                            placeItems: 'center'
                        }}
                    >
                        <JoulelingoCharacter mood={step.mood} size={120} />
                    </Box>
                    <Typography
                        id={titleId}
                        component="h2"
                        sx={{
                            ...HEADING_SX,
                            fontFamily: `${HEADING_SX.fontFamily} !important`,
                            fontSize: { xs: '1.9rem', sm: '2.3rem' },
                            color: GAME.ink
                        }}
                    >
                        {step.title}
                    </Typography>
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: '1.125rem',
                            maxWidth: 420
                        }}
                    >
                        {step.body}
                    </Typography>
                </Box>

                <Box aria-hidden="true" sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    {steps.map((s, i) => (
                        <Box
                            key={s.key}
                            sx={{
                                width: i === index ? 26 : 10,
                                height: 10,
                                borderRadius: 99,
                                bgcolor:
                                    i <= index ? GAME.bright : GAME.lineStrong,
                                transition: 'width 200ms ease'
                            }}
                        />
                    ))}
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        mt: 1,
                        width: '100%',
                        maxWidth: 320
                    }}
                >
                    <GameButton
                        fullWidth
                        disabled={saving}
                        onClick={() =>
                            last ? onDone() : setIndex((i) => i + 1)
                        }
                    >
                        {last ? 'Let’s go!' : 'Next'}
                    </GameButton>
                    {last ? null : (
                        <Button
                            onClick={onDone}
                            disabled={saving}
                            sx={{
                                fontFamily: BODY_FONT,
                                fontWeight: 800,
                                textTransform: 'none',
                                color: GAME.muted
                            }}
                        >
                            Skip
                        </Button>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    )
}

MeetJolt.propTypes = {
    quizOn: PropTypes.bool,
    saving: PropTypes.bool,
    onDone: PropTypes.func.isRequired
}
